import { Suspense, useState } from "react";
import useSWR from "swr";
import {
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import LinearLoader from "components/LinearLoader";
import { fetcher } from "utils/apiRequestWithErrorCode";
import refreshInterval from "../refreshInterval";
import { taskIsEnded } from "../../common/getMailboxRuleStatus";

import { Rule, MailboxRuleStats } from "@dashboard-v3/api";
import Search, { SearchCriteria } from "./Search";
import Header from "./Header";
import Body from "./Body";
import Loader from "./Loader";

const PAGE_LIMIT = 10;

export default function AccountsTable({ ruleId }: { ruleId: string }) {
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({});
  const { data: stats } = useSWR<MailboxRuleStats>(
    `/rules/${ruleId}/stats`,
    fetcher,
    {
      revalidateOnFocus: false,
      suspense: true,
      refreshInterval: latestData =>
        taskIsEnded(latestData?.executionStatus) ? 0 : refreshInterval,
    }
  );

  const { data: rule } = useSWR<Rule>(`/rules/${ruleId}`, fetcher, {
    revalidateOnFocus: false,
    suspense: true,
  });

  const columns =
    rule?.actionType === "REPLACE_ATTACHMENTS" ||
    rule?.actionType === "MOVE_EMAILS"
      ? 10
      : 9;

  return (
    <Suspense fallback={<LinearLoader />}>
      <Search
        values={searchCriteria}
        onSearch={criteria => setSearchCriteria(criteria)}
      />
      <Paper>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <Header item="email" align="left" />
                <Header item="status" align="left" />
                <Header item="messagesFiltered" withTooltip />
                <Header item="emailsDiscarded" withTooltip />
                <Header item="emailsProcessed" />
                <Header item="emailsUploaded" />
                <Header item="amountEmailsWithErrorFatal" />
                <Header item="amountEmailsToReprocess" />
                {rule?.actionType === "REPLACE_ATTACHMENTS" && (
                  <Header item="emailsProtected" />
                )}
                {rule?.actionType === "MOVE_EMAILS" && (
                  <Header item="emailsMoved" />
                )}
                <Header item="filesUploaded" />
              </TableRow>
            </TableHead>
            <Suspense fallback={<Loader rows={PAGE_LIMIT} colSpan={columns} />}>
              <Body
                actionType={rule?.actionType}
                executionStatus={stats?.executionStatus}
                ruleId={ruleId}
                searchCriteria={searchCriteria}
                pageLimit={PAGE_LIMIT}
              />
            </Suspense>
          </Table>
        </TableContainer>
      </Paper>
    </Suspense>
  );
}
